import { Ionicons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import React, { useCallback, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  View,
  useWindowDimensions,
} from 'react-native';

import { ThemedText } from './themed-text';
import { PressableScale } from './ui/pressable-scale';
import {
  useProviderNotifications,
  useUnreadNotificationsCount,
} from '@/hooks/use-provider-notifications';
import {
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
} from '@/hooks/use-mark-notification-read';
import { resolveMembership } from '@/hooks/use-membership';
import { resolveProviderNotificationTarget } from '@/lib/notification-navigation';
import { switchActingProvider } from '@/store/actingProviderStore';
import { useAuthStore } from '@/store/authStore';
import type { ProviderNotification, ProviderNotificationType } from '@/types/provider-notification';
import { cn } from '@/utils/cn';
import { useThemedHex } from '@/hooks/use-theme-colors';

const TYPE_STYLES: Record<
  ProviderNotificationType,
  { icon: keyof typeof Ionicons.glyphMap; iconColor: string; badgeClass: string }
> = {
  PAYMENT_REPORTED: {
    icon: 'cash-outline',
    iconColor: '#d97706',
    badgeClass: 'bg-amber-100',
  },
  PAYMENT_RECEIVED: {
    icon: 'checkmark-circle-outline',
    iconColor: '#059669',
    badgeClass: 'bg-emerald-100',
  },
  DISPUTE_CREATED: {
    icon: 'alert-circle-outline',
    iconColor: '#e11d48',
    badgeClass: 'bg-rose-100',
  },
  APPOINTMENT_REQUESTED: {
    icon: 'hourglass-outline',
    iconColor: '#7c3aed',
    badgeClass: 'bg-violet-100',
  },
  APPOINTMENT_BOOKED: {
    icon: 'calendar-outline',
    iconColor: '#0284c7',
    badgeClass: 'bg-sky-100',
  },
  APPOINTMENT_CANCELLED_BY_CLIENT: {
    icon: 'calendar-clear-outline',
    iconColor: '#e11d48',
    badgeClass: 'bg-rose-100',
  },
  RECURRING_REQUESTED: {
    icon: 'repeat-outline',
    iconColor: '#0d9488',
    badgeClass: 'bg-teal-100',
  },
  PLAN_USAGE_NEAR_LIMIT: {
    icon: 'speedometer-outline',
    iconColor: '#d97706',
    badgeClass: 'bg-amber-100',
  },
  PLAN_LIMIT_REACHED: {
    icon: 'speedometer-outline',
    iconColor: '#e11d48',
    badgeClass: 'bg-rose-100',
  },
  PAYMENT_ACCOUNT_ACTIVE: {
    icon: 'checkmark-circle-outline',
    iconColor: '#059669',
    badgeClass: 'bg-emerald-100',
  },
  PAYMENT_ACCOUNT_ACTION_REQUIRED: {
    icon: 'alert-circle-outline',
    iconColor: '#d97706',
    badgeClass: 'bg-amber-100',
  },
  PAYMENT_ACCOUNT_RESTRICTED: {
    icon: 'alert-circle-outline',
    iconColor: '#e11d48',
    badgeClass: 'bg-rose-100',
  },
  PAYMENT_PAYOUT_FAILED: {
    icon: 'alert-circle-outline',
    iconColor: '#e11d48',
    badgeClass: 'bg-rose-100',
  },
};

function formatRelativeTime(dateStr: string) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Agora';
  if (diffMins < 60) return `${diffMins}min`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
}

export function ProviderNotificationBell({
  onClose,
  className = '',
  iconSize = 24,
}: {
  onClose?: () => void;
  className?: string;
  /** Menor na sidebar, onde o sino divide a linha com o perfil. */
  iconSize?: number;
}) {
  const th = useThemedHex();
  const router = useRouter();
  const [showDropdown, setShowDropdown] = useState(false);
  const triggerRef = useRef<View>(null);
  const [anchor, setAnchor] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const { data: count = 0 } = useUnreadNotificationsCount();
  const { data: notifications = [], isLoading } = useProviderNotifications();
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();

  /**
   * O sino mudou de lugar (rodapé da sidebar no desktop, faixa da página no
   * mobile), então o painel é ancorado no gatilho medido — não mais colado no
   * canto superior direito da tela.
   */
  const openDropdown = useCallback(() => {
    const node = triggerRef.current as any;
    if (!node) {
      setAnchor(null);
      setShowDropdown(true);
      return;
    }

    if (Platform.OS === 'web') {
      const rect = node.getBoundingClientRect?.();
      setAnchor(rect ? { x: rect.x, y: rect.y, width: rect.width, height: rect.height } : null);
      setShowDropdown(true);
      return;
    }

    if (typeof node.measureInWindow === 'function') {
      node.measureInWindow((x: number, y: number, width: number, height: number) => {
        setAnchor({ x, y, width, height });
        setShowDropdown(true);
      });
      return;
    }

    setAnchor(null);
    setShowDropdown(true);
  }, []);

  const PANEL_MAX_HEIGHT = 560;
  const EDGE = 16;
  const GAP = 8;
  const panelWidth = Math.min(400, windowWidth - EDGE * 2);

  const panelPosition = (() => {
    if (!anchor) {
      // Sem medida: mantém o comportamento antigo (topo à direita).
      return {
        top: 80,
        bottom: undefined as number | undefined,
        left: Math.max(EDGE, windowWidth - panelWidth - EDGE),
        maxHeight: PANEL_MAX_HEIGHT,
      };
    }

    const spaceBelow = windowHeight - (anchor.y + anchor.height) - EDGE;
    const spaceAbove = anchor.y - EDGE;
    const opensAbove = spaceBelow < 280 && spaceAbove > spaceBelow;

    // Gatilho na metade esquerda (sidebar) abre para a direita; na direita
    // (faixa da página), alinha pela borda direita do gatilho.
    const alignsLeft = anchor.x + anchor.width / 2 < windowWidth / 2;
    const rawLeft = alignsLeft ? anchor.x : anchor.x + anchor.width - panelWidth;
    const left = Math.min(Math.max(rawLeft, EDGE), Math.max(EDGE, windowWidth - panelWidth - EDGE));

    // Abrindo para cima, o painel é preso pela BASE: se posicionasse pelo topo
    // usando a altura máxima, um painel curto ficaria colado no teto da tela,
    // bem longe do sino.
    return opensAbove
      ? {
          bottom: Math.max(EDGE, windowHeight - anchor.y + GAP),
          top: undefined as number | undefined,
          left,
          maxHeight: Math.min(PANEL_MAX_HEIGHT, spaceAbove),
        }
      : {
          bottom: undefined as number | undefined,
          top: anchor.y + anchor.height + GAP,
          left,
          maxHeight: Math.min(PANEL_MAX_HEIGHT, spaceBelow),
        };
  })();

  const handleNotificationPress = (notification: ProviderNotification) => {
    markAsRead.mutate(notification.id);
    setShowDropdown(false);
    onClose?.();

    // Aviso da equipe: o agendamento é do membro, então o foco vai para ele
    // antes de abrir — senão o detalhe buscaria na agenda do dono.
    const about = notification.aboutProvider;
    if (about && resolveMembership(useAuthStore.getState().user?.membership).can('actAs')) {
      switchActingProvider(about);
    }

    const target = resolveProviderNotificationTarget(notification);
    router.push(target as any);
  };

  const handleMarkAllRead = () => {
    markAllAsRead.mutate();
    setShowDropdown(false);
    onClose?.();
  };

  return (
    <>
      <Pressable
        ref={triggerRef}
        collapsable={false}
        onPress={openDropdown}
        className={`relative rounded-lg p-2 active:bg-neutral-100 ${className}`}
        accessibilityRole="button"
        accessibilityLabel="Notificações"
      >
        <Ionicons name="notifications-outline" size={iconSize} className="text-foreground" />
        {count > 0 && (
          <View className="absolute -right-1 -top-1 min-w-[18px] items-center justify-center rounded-full bg-primary px-1.5 py-0.5">
            <ThemedText className="font-semibold text-xs text-primary-foreground">
              {count > 99 ? '99+' : count}
            </ThemedText>
          </View>
        )}
      </Pressable>

      <Modal
        transparent
        animationType="fade"
        // Sem isso o Android mede o gatilho contando a status bar, mas desenha
        // o modal abaixo dela — o painel descia pela altura da barra.
        statusBarTranslucent
        visible={showDropdown}
        onRequestClose={() => setShowDropdown(false)}
      >
        <Pressable className="flex-1 bg-black/40" onPress={() => setShowDropdown(false)}>
          <Pressable
            onPress={(e) => e.stopPropagation()}
            className="absolute overflow-hidden rounded-2xl border border-border bg-neutral-50 shadow-2xl"
            style={{
              // Na web o retângulo do gatilho é da viewport, então o painel
              // precisa ser fixed — absolute o prende ao pai do portal do
              // Modal, que nem sempre começa em 0,0.
              ...(Platform.OS === 'web' ? ({ position: 'fixed' } as any) : null),
              top: panelPosition.top,
              bottom: panelPosition.bottom,
              left: panelPosition.left,
              width: panelWidth,
              maxHeight: panelPosition.maxHeight,
            }}
          >
            <View className="flex-row items-center justify-between border-b border-border bg-card px-5 py-4">
              <View className="flex-row items-center gap-2">
                <ThemedText className="font-bold text-lg text-foreground">Notificações</ThemedText>
                {count > 0 && (
                  <View className="rounded-full bg-primary/10 px-2 py-0.5">
                    <ThemedText className="font-semibold text-xs text-primary">
                      {count > 99 ? '99+' : count} nova{count > 1 ? 's' : ''}
                    </ThemedText>
                  </View>
                )}
              </View>
              {count > 0 && (
                <PressableScale
                  onPress={handleMarkAllRead}
                  disabled={markAllAsRead.isPending}
                  className="rounded-full bg-primary/10 px-3 py-1.5 active:bg-primary/20"
                >
                  <ThemedText className="font-semibold text-xs text-primary">
                    Marcar todas
                  </ThemedText>
                </PressableScale>
              )}
            </View>

            {isLoading ? (
              <View className="items-center justify-center py-16">
                <ActivityIndicator size="small" className="text-primary" />
              </View>
            ) : notifications.length === 0 ? (
              <View className="items-center justify-center gap-3 px-6 py-16">
                <View className="h-14 w-14 items-center justify-center rounded-full bg-neutral-100">
                  <Ionicons
                    className="text-neutral-400"
                    name="notifications-off-outline"
                    size={26}
                  />
                </View>
                <ThemedText className="text-center font-medium text-foreground">
                  Nenhuma notificação
                </ThemedText>
                <ThemedText className="text-center text-sm text-neutral-500">
                  Avisos de pagamentos e contestações aparecem aqui.
                </ThemedText>
              </View>
            ) : (
              <ScrollView
                keyboardShouldPersistTaps="handled"
                // Sobra do painel depois do cabeçalho: o teto acompanha o
                // espaço real que o gatilho deixou na tela.
                style={{ maxHeight: Math.max(160, panelPosition.maxHeight - 64) }}
                contentContainerClassName="gap-2 p-3"
                showsVerticalScrollIndicator={false}
              >
                {notifications.map((n) => {
                  const isUnread = !n.readAt;
                  const style = TYPE_STYLES[n.type] ?? TYPE_STYLES.PAYMENT_REPORTED;

                  return (
                    <PressableScale
                      key={n.id}
                      onPress={() => handleNotificationPress(n)}
                      className={cn(
                        'flex-row items-start gap-3 rounded-2xl border p-3.5',
                        isUnread
                          ? 'border-primary/20 bg-card shadow-sm'
                          : 'border-border/60 bg-card/60',
                      )}
                    >
                      <View
                        className={cn(
                          'h-10 w-10 items-center justify-center rounded-full',
                          isUnread ? style.badgeClass : 'bg-neutral-100',
                        )}
                      >
                        <Ionicons
                          name={style.icon}
                          size={20}
                          color={isUnread ? style.iconColor : th('#a3a3a3')}
                        />
                      </View>

                      <View className="flex-1">
                        <View className="flex-row items-center justify-between gap-2">
                          <ThemedText
                            className={cn(
                              'flex-1 text-sm text-foreground',
                              isUnread ? 'font-semibold' : 'font-medium',
                            )}
                            numberOfLines={1}
                          >
                            {n.title}
                          </ThemedText>
                          <ThemedText className="text-xs text-neutral-400">
                            {formatRelativeTime(n.createdAt)}
                          </ThemedText>
                        </View>
                        <ThemedText
                          className="mt-1 text-xs leading-4 text-neutral-500"
                          numberOfLines={2}
                        >
                          {n.message}
                        </ThemedText>
                      </View>

                      {isUnread && <View className="mt-1.5 h-2 w-2 rounded-full bg-primary" />}
                    </PressableScale>
                  );
                })}
              </ScrollView>
            )}
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}
